import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authApi } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    // If returning from Google OAuth, let AuthCallback exchange the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const startToken = localStorage.getItem("wechr_token");
    try {
      const { data } = await authApi.me();
      setUser(data);
    } catch (e) {
      // Only clear if nothing changed during this request — avoids clobbering a
      // session established concurrently (e.g. the email-verification flow).
      if (localStorage.getItem("wechr_token") === startToken) {
        if (startToken) localStorage.removeItem("wechr_token");
        setUser((u) => u || null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = async (email, password) => {
    const { data } = await authApi.login({ email, password });
    localStorage.setItem("wechr_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const signup = async (payload) => {
    // Registration now requires email verification — no auto-login.
    const { data } = await authApi.signup(payload);
    return data; // { requires_verification, email, message }
  };

  const verifyEmail = async (token) => {
    const { data } = await authApi.verifyEmail(token);
    localStorage.setItem("wechr_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const loginWithGoogleSession = async (session_id) => {
    const { data } = await authApi.googleSession(session_id);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await authApi.logout(); } catch (e) { /* ignore */ }
    localStorage.removeItem("wechr_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, verifyEmail, loginWithGoogleSession, logout, isAdmin: user?.role === "admin" }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
